import {
  AfterViewInit,
  Component,
  computed,
  Inject,
  OnDestroy,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTree } from '@angular/material/tree';
import { firstValueFrom } from 'rxjs';
import { BucketService, FileNode, FolderNode } from 'src/app/services/bucket/bucket.service';
import { ProgressBarOverlayComponent } from '../progress-bar-overlay/progress-bar-overlay.component';

//Stato UI
interface FolderState {
  checked: boolean;
  indeterminate: boolean;
}
export interface FolderNodeWithCheck extends FolderNode, FolderState {
  folders: FolderNodeWithCheck[];
  selected: boolean;
  parent?: FolderNodeWithCheck;
}

@Component({
  selector: 'app-dialog-selezione-documenti-per-ai',
  standalone: false,

  templateUrl: './dialog-selezione-documenti-per-ai.component.html',
  styleUrl: './dialog-selezione-documenti-per-ai.component.scss',
})
export class DialogSelezioneDocumentiPerAiComponent implements OnDestroy, AfterViewInit {
  @ViewChild('tree') tree!: MatTree<FolderNodeWithCheck>;
  @ViewChild(ProgressBarOverlayComponent) progressBarOverlay!: ProgressBarOverlayComponent;

  protected isDisable = !!this.data.idModulo;

  /** * Signal contenente la struttura dati delle cartelle.
   * Viene popolato una sola volta all'avvio.
   * L'ho definito readonly, cosi da non scriversci dentro
   * Poichè il MatTree perdeva il riferimento dell'alberatura
   */
  protected readonly folders = signal<FolderNodeWithCheck[]>([]);

  /** * Id dei folder selezionati, che sarà sempre l'ultimo figlio del ramo
   * Utilizzo un Set per ottimizzare le operazioni di aggiunta, rimozione e ricerca.
   */
  protected readonly selectedFoldersIds = signal<Set<string>>(new Set());

  /**
   * Lista flatten di file estratti solo dalle cartelle selezionate.
   * Si aggiorna automaticamente al variare delle selezioni.
   */
  readonly listaFiles = computed(() => {
    // Accediamo a treeUiState per registrare la dipendenza reattiva
    const stateMap = this.treeUiState();
    const folders = this.folders();
    const selectedIds = this.selectedFoldersIds();

    if (folders.length === 0) return [];

    return this.flattenFiles(folders, selectedIds);
  });
  /**
   * Computed che viene eseguito quando mi varia la lista dei @selectedFoldersIds
   * Mi ritorna una mappa dove {key = idFolder, value = {checked, indeterminate}}
   */
  readonly treeUiState = computed(() => {
    const stateMap = new Map<string, { checked: boolean; indeterminate: boolean }>();
    const foldersSelected = this.selectedFoldersIds();
    const root = untracked(() => this.folders());

    // Loop ricorsivo per ogni folder
    root.forEach((folder) => this.computeFolderState(folder, foldersSelected, stateMap));

    return stateMap;
  });

  /**
   * Calcola ricorsivamente lo stato di una cartella e dei suoi figli.
   */
  private computeFolderState(
    folder: FolderNodeWithCheck,
    selectedIds: Set<string>,
    stateMap: Map<string, FolderState>,
  ): boolean {
    if (this.hasSottoCartelle(folder)) {
      return this.processParentFolderWithChild(folder, selectedIds, stateMap);
    }

    return this.processParentFolder(folder, selectedIds, stateMap);
  }

  /**
   * CASO A: Gestisce una cartella Genitore (senza sottocartelle).
   * Se esiste nella mappa dei selezionati allora checked, altrimenti unchecked
   * Una cartella Gentiore se non ha sottocartelle, non sarà mai indeterminate
   */
  private processParentFolder(
    folder: FolderNodeWithCheck,
    selectedIds: Set<string>,
    stateMap: Map<string, FolderState>,
  ): boolean {
    const isChecked = selectedIds.has(folder.id);

    stateMap.set(folder.id, { checked: isChecked, indeterminate: false });

    return isChecked;
  }

  /**
   * CASO B: Gestisce una cartella genitore, aggregando lo stato dei figli.
   */
  private processParentFolderWithChild(
    folder: FolderNodeWithCheck,
    selectedIds: Set<string>,
    stateMap: Map<string, FolderState>,
  ): boolean {
    let checkedChildrenCount = 0;
    let hasIndeterminateChild = false;

    folder.folders.forEach((child) => {
      const isChildFullChecked = this.computeFolderState(child, selectedIds, stateMap);
      if (isChildFullChecked) checkedChildrenCount++;
      if (stateMap.get(child.id)?.indeterminate) hasIndeterminateChild = true;
    });

    const allChildrenChecked = checkedChildrenCount === folder.folders.length;
    const noChildrenChecked = checkedChildrenCount === 0;

    // Un padre è 'checked' SOLO SE tutti i figli sono checked
    // E se l'ID del padre è presente nel Set (se non è stato decheckato manualmente)
    const isChecked = allChildrenChecked && selectedIds.has(folder.id);

    // Un padre è 'indeterminate' se:
    // - Ha figli misti (alcuni checked, alcuni no)
    // - OPPURE ha tutti i figli checked ma il padre stesso è stato rimosso dal Set
    const isIndeterminate =
      hasIndeterminateChild ||
      (!noChildrenChecked && !allChildrenChecked) ||
      (allChildrenChecked && !selectedIds.has(folder.id));

    stateMap.set(folder.id, {
      checked: isChecked,
      indeterminate: isIndeterminate,
    });

    return isChecked;
  }

  /**
   * Estrae gli id dei "Genitori Superiori" selezionati.
   * Se un padre è selezionato, prende il suo id e NON scende nei figli.
   * Se un padre è indeterminato, allora prendo gli id dei figlioli
   */
  readonly idMainFoldersSelected = computed(() => {
    const stateMap = this.treeUiState();
    const root = this.folders();
    const ids: string[] = [];

    const collectIds = (folder: FolderNodeWithCheck) => {
      const state = stateMap.get(folder.id);
      if (state?.checked) {
        ids.push(folder.id); // Prende il padre e si ferma
      } else if (state?.indeterminate) {
        folder.folders?.forEach((child) => collectIds(child)); // Scende ai figli
      }
    };

    root.forEach(collectIds);
    return ids;
  });

  constructor(
    private readonly dialogRef: MatDialogRef<DialogSelezioneDocumentiPerAiComponent>,
    private readonly bucketService: BucketService,
    @Inject(MAT_DIALOG_DATA)
    public data: { idModulo: string | null; selectedFolderIds: string[] },
  ) {}

  async ngAfterViewInit(): Promise<void> {
    await this.loadInitialData();
    if (this.data?.selectedFolderIds) {
      this.initSelectionFromBackend(this.data?.selectedFolderIds);
    }
    this.expandAllTree();
  }

  /**
   * Espando la tree totale
   */
  private expandAllTree() {
    /*    afterNextRender(() => {}); */

    setTimeout(() => {
      if (this.folders().length > 0) {
        this.tree.expandAll();
      }
    }, 0);
  }

  /**
   * Restituisce lo stato 'checked' per un nodo specifico leggendolo dalla Map calcolata.
   */
  getChecked(node: FolderNodeWithCheck): boolean {
    return this.treeUiState().get(node.id)?.checked ?? false;
  }

  /**
   * Restituisce lo stato 'indeterminate' per un nodo specifico leggendolo dalla Map calcolata.
   */
  getIndeterminate(node: FolderNodeWithCheck): boolean {
    return this.treeUiState().get(node.id)?.indeterminate ?? false;
  }

  /**
   * Get delle cartelle da visualizzare
   */
  private async loadInitialData() {
    this.progressBarOverlay?.show('Caricamento documenti...');
    try {
      const result: FolderNodeWithCheck = await firstValueFrom(
        this.bucketService.getFoldersForModuli(this.data.idModulo),
      );
      this.folders.set(result.folders[0].folders);
    } catch (error) {
      console.error('Errore caricamento cartelle:', error);
    } finally {
      this.progressBarOverlay?.hide();
    }
  }

  /**
   * Azione sulla checkbox. (check & decheck)
   *
   * @selectedFoldersIds -> signal con gli ID dei folders gia selezionati
   *
   * @newSelectedFoldersIds = @selectedFoldersIds + l'elaborazione
   * sull'(check & decheck) del @folder , viene elaborato con il metodo @updateSelectionRecursive
   * In sintesi aggiunge / rimuove l'ID del folder selezionato e i suoi sottofolders.
   * Quando viene ritornato, mi trigghera il signal @selectedFoldersIds grazie all'update, che farà partire
   * il computed di calcolo checked e indeterminate, tramite il computed @treeUiState
   *
   * @param folder azionato
   * @param checked true / false
   * @returns newSelectedFoldersIds + trigger sul computed treeUiState
   */
  toggleFolder(folder: FolderNodeWithCheck, checked: boolean): void {
    this.selectedFoldersIds.update((oldFoldersIds) => {
      // Mi salvo i folder già selezionati
      const newSelectedFoldersIds = new Set(oldFoldersIds);

      // aggiorno newSelectedFoldersIds
      this.updateSelectionRecursive(folder, checked, newSelectedFoldersIds);

      return newSelectedFoldersIds;
    });
  }
  /**
   * Mi valorizza la newSelectedFoldersIds con l'id del ultimo figlio che trova,
   * questo per poi fare l'auto check decheck sui suoi parent se necessario.
   * @param folder azionato
   * @param checked true / false
   * @param newSelectedFoldersIds folders già checkatti
   */
  private updateSelectionRecursive(
    folder: FolderNodeWithCheck,
    checked: boolean,
    newSelectedFoldersIds: Set<string>,
  ): void {
    if (checked) {
      // Quando seleziono, aggiungo l'ID del nodo corrente (sia esso padre o figlio)
      newSelectedFoldersIds.add(folder.id);

      // E scendo ricorsivamente nei figli
      folder.folders?.forEach((sub) =>
        this.updateSelectionRecursive(sub, true, newSelectedFoldersIds),
      );
    } else {
      // Quando deseleziono, rimuovo SOLO l'ID del nodo corrente
      newSelectedFoldersIds.delete(folder.id);
      // NON scendo nei figli: rimangono nel Set e il padre diventerà 'indeterminate'
    }
  }

  /**
   * Creo una lista di file per i folder checkati
   *
   * @param nodes Lista folders da elaborare
   * @param selectedFoldersIds mappa con gli id del filgio più profondo del parent folder
   * @param accumulator Per evitare di creare un numero di array per n call ricorsione. Potrebbe rallentare il DOM
   * essendo che il JS creerebbe N* array per x di spread di file.
   * @returns la lista di file da visualizzare
   */
  private flattenFiles(
    nodes: FolderNodeWithCheck[],
    selectedFoldersIds: Set<string>,
    accumulator: FileNode[] = [],
  ): FileNode[] {
    // Mappa degli elementi, con check uncheck e indeterminate
    const stateMap = this.treeUiState();

    for (const node of nodes) {
      const state = stateMap.get(node.id);

      //Se la cartella è checked, allora aggiungo i file nella lista
      if (state?.checked) {
        if (node.files) {
          accumulator.push(...node.files); // Modifica l'array esistente, molto più veloce
        }
        // Se ha sotto cartelle, scavo per prendere i file successivi con una ricorssione
        if (node.folders) {
          this.flattenFiles(node.folders, selectedFoldersIds, accumulator);
        }

        // Se è indeterminate ci sarà almeno un folder checkato, quindi scavo con ricorsiojne per trovare i file
      } else if (state?.indeterminate) {
        if (node.folders) {
          this.flattenFiles(node.folders, selectedFoldersIds, accumulator);
        }
      }
    }

    return accumulator;
  }

  /**
   * Metodo da chiamare all'avvio (es. nel costruttore o dopo la chiamata API)
   * Se trova un ID padre nella lista del BE, seleziona lui e tutta la sua discendenza.
   * * @param idsFromBE - Array di stringhe (ID) ricevuti dal backend
   */
  initSelectionFromBackend(idsFromBE: string[]): void {
    const newSet = new Set<string>();
    const root = this.folders();

    // Funzione di ricerca ricorsiva nell'albero
    const searchAndSelect = (folder: FolderNodeWithCheck, parentAlreadyMatched: boolean) => {
      // 1. Verifichiamo se questa cartella è tra quelle del BE
      //    O se il suo genitore era già stato selezionato.
      const isMatch = parentAlreadyMatched || idsFromBE.includes(folder.id);

      if (isMatch) {
        // Se c'è un match, usiamo la logica di "selezione totale" per questa cartella e i suoi figli
        this.updateSelectionRecursive(folder, true, newSet);
      } else {
        // Se non c'è un match, continuiamo a cercare nelle sottocartelle
        folder.folders?.forEach((sub) => searchAndSelect(sub, false));
      }
    };

    // Avviamo la ricerca per ogni radice
    root.forEach((f) => searchAndSelect(f, false));

    // Aggiorniamo il signal finale
    this.selectedFoldersIds.set(newSet);
  }

  protected childrenAccessor = (node: any) => node.folders ?? [];
  /** Verifica se il nodo ha figli */
  protected hasSottoCartelle = (node: any) => !!node.folders?.length;
  /** Verifica se il nodo contiene file */
  protected hasFile = (node: any) => !!node.files?.length;
  /** Restituisce il nome dell'icona Material basata sullo stato del nodo */
  protected folderIcon = (node: any) =>
    this.hasSottoCartelle(node) || this.hasFile(node) ? 'folder' : 'folder_open';

  /** Gestisce l'espansione/collasso manuale tramite icona */
  protected expandCollapseFolder(folder: any): void {
    this.tree.isExpanded(folder) ? this.tree.collapse(folder) : this.tree.expand(folder);
  }

  /** Ottimizza il rendering del tree identificando i nodi in modo univoco */
  protected trackById = (index: number, node: FolderNodeWithCheck) => node.id;

  /** Reset dei segnali alla distruzione del componente per prevenire leak di memoria */
  ngOnDestroy(): void {
    this.folders.set([]);
  }

  protected salva() {
    const result = {
      isSaved: true,
      idMainFoldersSelected: this.idMainFoldersSelected(),
    };
    this.dialogRef.close(result);
  }
}
